import { useEffect, useMemo } from 'react';
import { useModal } from '@stores';
import { ModalContent, ModalFooter } from '@components/modal';
import ContactFields from '@forms/components/Form/contact-fields/ContactFields';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useForm, FormProvider } from 'react-hook-form';
import ErrorSummary from '@components/error-summary/ErrorSummary';
import { contactInputValidation } from '@src/utils/validation/inputValidation.contact';
import { formatUSPhoneNumber } from '@src/utils/helpers';
import { ContactTypes, ProfileRoles } from '@src/utils/enums';
import {
  hasDuplicateAgent,
  hasMatchingApplicantAgent,
  isCurrentUser,
} from '@forms/components/modals/_shared/UserProfileAgent';

const nameLabels = ['First Name', 'Last Name'];
const addressLabels = ['Address One', 'City', 'State', 'Zip Code', 'Country'];

const addressFieldNames = ['address', 'addressTwo', 'city', 'state', 'zipcode', 'country'];
const phoneOneFieldNames = ['phoneOneType', 'phoneOneCountryCode', 'phoneOne'];
const phoneTwoFieldNames = ['phoneTwoType', 'phoneTwoCountryCode', 'phoneTwo'];

const validateContact = ({ contactType, userProfileData, contactFields, rowData, rowIndex, setError, clearErrors }) => {
  const isUserAgent = userProfileData?.role === ProfileRoles.Agent;
  const isUserTheContact = isCurrentUser(userProfileData, contactFields);
  const isDuplicate = hasDuplicateAgent(rowData, contactFields, rowIndex);
  const hasApplicantAgentMatch = hasMatchingApplicantAgent(rowData, contactFields, rowIndex);

  if ([ContactTypes.Agent, ContactTypes.Applicant].includes(contactType) && !isUserAgent && isUserTheContact) {
    setError('firstName', { type: 'manual', message: 'You cannot add yourself as an agent' });
    return false;
  }

  if (contactType === ContactTypes.Agent && isDuplicate) {
    setError('firstName', { type: 'manual', message: 'Duplicate Agents are not allowed' });
    return false;
  }

  if ([ContactTypes.Agent, ContactTypes.Applicant].includes(contactType) && hasApplicantAgentMatch) {
    setError('firstName', {
      type: 'manual',
      message: 'The Applicant cannot also be listed as an Agent',
    });
    return false;
  }

  clearErrors('firstName');
  return true;
};

const AddContactModal = ({
  addRowData: onSave,
  contactType,
  edit,
  data,
  onUpdate,
  rowData,
  rowIndex,
  id,
  isReadOnly,
  userProfileData,
  questionType,
}) => {
  const { closeModal } = useModal();
  const isBasicContact = questionType === ContactTypes.BasicContact;

  const defaultValues = {
    address: data?.address ?? null,
    addressTwo: data?.addressTwo ?? null,
    city: data?.city ?? null,
    company: data?.company ?? null,
    contactType: data?.contactType ?? contactType,
    country: data?.country ?? 'US',
    emailAddress: data?.emailAddress ?? null,
    faxCountryCode: data?.faxCountryCode ?? '1',
    faxPhone:
      data?.faxCountryCode === '1' && data?.faxPhone ? formatUSPhoneNumber(data?.faxPhone) : (data?.faxPhone ?? null),
    firstName: data?.firstName ?? null,
    lastName: data?.lastName ?? null,
    middleName: data?.middleName ?? null,
    phoneOne:
      data?.phoneOneCountryCode === '1' && data?.phoneOne
        ? formatUSPhoneNumber(data?.phoneOne)
        : (data?.phoneOne ?? null),
    phoneOneCountryCode: data?.phoneOneCountryCode ?? (isBasicContact ? null : '1'),
    phoneOneExtension: data?.phoneOneExtension ?? null,
    phoneOneType: data?.phoneOneType ?? null,
    phoneTwo:
      data?.phoneTwoCountryCode === '1' && data?.phoneTwo
        ? formatUSPhoneNumber(data?.phoneTwo)
        : (data?.phoneTwo ?? null),
    phoneTwoCountryCode: data?.phoneTwoCountryCode ?? null,
    phoneTwoExtension: data?.phoneTwoExtension ?? null,
    phoneTwoType: data?.phoneTwoType ?? null,
    salutation: data?.salutation ?? null,
    state: data?.state ?? null,
    zipcode: data?.zipcode ?? null,
  };

  const basicContactSchema = yup.object().shape(
    {
      ...contactInputValidation.nameRequired,
      ...contactInputValidation.addressRequired,
      ...contactInputValidation.phoneOneOptional,
      ...contactInputValidation.faxOptional,
      ...contactInputValidation.emailOptional,
    },
    [...contactInputValidation.phoneOneOptionalDependencies, contactInputValidation.faxOptionalDependencies]
  );

  // Schema for all other contact types
  const standardSchema = yup.object().shape(
    {
      ...contactInputValidation.nameRequired,
      ...contactInputValidation.addressRequired,
      ...contactInputValidation.phoneRequired,
      ...contactInputValidation.faxOptional,
      ...contactInputValidation.emailRequired,
    },
    [contactInputValidation.faxOptionalDependencies]
  );
  const schema = useMemo(() => {
    return isBasicContact ? basicContactSchema : standardSchema;
  }, [isBasicContact, basicContactSchema, standardSchema]);

  const methods = useForm({
    resolver: yupResolver(schema),
    mode: 'onBlur',
    defaultValues,
  });
  const {
    formState: { errors, isValid },
    setError,
    clearErrors,
    watch,
    setFocus,
    trigger,
    getValues,
  } = methods;

  useEffect(() => {
    edit && trigger();
  }, [edit, trigger]);

  const addressFields = watch(addressFieldNames);
  const phoneOneFields = watch(phoneOneFieldNames);
  const phoneTwoFields = watch(phoneTwoFieldNames);

  const addressFilled = useMemo(() => addressFields.some((field) => !!field?.trim()), [addressFields]);
  const phoneOneFilled = useMemo(() => phoneOneFields.some((field) => !!field?.trim()), [phoneOneFields]);
  const phoneTwoFilled = useMemo(() => phoneTwoFields.some((field) => !!field?.trim()), [phoneTwoFields]);

  const requiredFields = useMemo(() => {
    const address = addressFilled ? addressLabels : [];
    return [...nameLabels, ...address];
  }, [addressFilled]);

  const handleContactSave = () => {
    if (isValid) {
      const contactFields = getValues();
      const formContactType = contactFields.contactType ?? contactType;
      const isValidContact = validateContact({
        contactType: formContactType,
        userProfileData,
        contactFields,
        rowData,
        rowIndex,
        setError,
        clearErrors,
      });

      if (!isValidContact) return;

      const contactFieldsWithIDs = {
        ...data,
        ...contactFields,
        contactID: data?.contactID ?? undefined,
        contactType: formContactType,
        createdBy: data?.createdBy ?? undefined,
        faxPhone: contactFields?.faxPhone?.replace(/\D/g, '') ?? undefined,
        phoneOne: contactFields?.phoneOne?.replace(/\D/g, '') ?? undefined,
        phoneTwo: contactFields?.phoneTwo?.replace(/\D/g, '') ?? undefined,
        requestID: data?.requestID ?? undefined,
        version: data?.version ?? undefined,
      };

      if (edit) {
        const updatedDataArr = rowData.map((item, idx) => {
          const targetIndex = data.contactID ? rowData.findIndex((el) => el.contactID === id) : id;
          return idx === targetIndex ? contactFieldsWithIDs : item;
        });
        onUpdate(updatedDataArr);
      } else {
        onSave(contactFieldsWithIDs, onUpdate);
      }
      closeModal();
    } else {
      trigger();
    }
  };

  useEffect(() => {
    !addressFilled && trigger(addressFieldNames);
  }, [addressFilled, trigger]);

  useEffect(() => {
    !phoneOneFilled && trigger(phoneOneFieldNames);
  }, [phoneOneFilled, trigger]);

  useEffect(() => {
    !phoneTwoFilled && trigger(phoneTwoFieldNames);
  }, [phoneTwoFilled, trigger]);

  useEffect(() => {
    if (errors?.[Object.keys(errors)[0]]?.['ref']?.focus) {
      errors?.[Object.keys(errors)[0]]?.['ref']?.focus();
    }

    setFocus(errors?.[Object.keys(errors)[0]]?.['ref']?.['id']);
  }, [errors, setFocus]);

  return (
    <FormProvider {...methods}>
        
      <ModalContent
        hasCloseButton={isReadOnly}
        title={`${edit ? 'Edit' : 'Add'} ${edit ? data?.contactType : contactType}`}
      >
                {errors && <ErrorSummary errors={errors} modalID='contactsModal' type='modal' />}         
        <section className='modal-body' id='contactsModal'>
          <ContactFields
            edit={edit}
            isReadOnly={isReadOnly}
            label={edit ? data?.contactType : contactType}
            type={edit ? data?.contactType : contactType}
            showButton={contactType === ContactTypes.Agent && userProfileData?.role === ContactTypes.Agent}
            {...(questionType === ContactTypes.BasicContact ? { requiredFields } : {})}
          />
                  
        </section>
                
        <ModalFooter
          customClosingLogic
          onSave={handleContactSave}
          saveText={edit ? 'Apply Changes' : 'Add'}
          showCancelButton={!isReadOnly}
          showSaveButton={!isReadOnly}
        />{' '}
              
      </ModalContent>{' '}
          
    </FormProvider>
  );
};

export default AddContactModal;
